import React from 'react';
import { Button, Switch, Radio } from 'antd';

import { useUserRole } from '../hooks/usePrerenderUser';
import type { UserRole } from '@prerender/prerender-shared/FE';

export function useIsDisabledForRole(disabledFor?: UserRole | readonly UserRole[]) {
  const userRole = useUserRole();

  if (!disabledFor) return false;

  if (Array.isArray(disabledFor)) {
    return disabledFor.includes(userRole);
  }

  return disabledFor === userRole;
}

// Allow-list companion to useIsDisabledForRole, for a control that has no `disabledFor` prop to
// pass a deny-list to. Both forms are safe against a role added to UserRole later, because every
// deny-list a control passes is now the computed complement of the server's own allow-list
// (features/auth/roleCapabilities.ts), so a new role is refused on both sides by omission.
export function useIsRoleIn(allowed: readonly UserRole[]) {
  const userRole = useUserRole();

  return allowed.includes(userRole);
}

type ButtonWithRoleProps = {
  disabledFor: UserRole | readonly UserRole[];
} & React.ComponentPropsWithoutRef<typeof Button>;

const ButtonWithRole = React.forwardRef<
  HTMLButtonElement | HTMLAnchorElement,
  React.PropsWithChildren<ButtonWithRoleProps>
>(function ButtonWithRole({ disabledFor, children = null, ...props }, ref) {
  const disabledForRole = useIsDisabledForRole(disabledFor);
  const disabled = disabledForRole || props.disabled;

  return (
    <Button {...props} ref={ref} disabled={disabled} onClick={disabled ? undefined : props.onClick}>
      {children}
    </Button>
  );
});

type SwitchWithRoleProps = {
  disabledFor: UserRole | readonly UserRole[];
} & React.ComponentPropsWithoutRef<typeof Switch>;

export const SwitchWithRole = React.forwardRef<HTMLButtonElement, SwitchWithRoleProps>(function SwitchWithRole(
  { disabledFor, ...props },
  ref
) {
  const disabledForRole = useIsDisabledForRole(disabledFor);
  const disabled = disabledForRole || props.disabled;
  return <Switch {...props} ref={ref} disabled={disabled} onClick={disabled ? undefined : props.onClick} />;
});

type RadioGroupWithRoleProps = {
  disabledFor: UserRole | readonly UserRole[];
} & React.ComponentProps<typeof Radio.Group>;

export function RadioGroupWithRole({ disabledFor, ...props }: RadioGroupWithRoleProps) {
  const disabledForRole = useIsDisabledForRole(disabledFor);
  const disabled = disabledForRole || props.disabled;

  return <Radio.Group {...props} disabled={disabled} onChange={disabled ? undefined : props.onChange} />;
}

export default ButtonWithRole;
